/**
 * Reading an uploaded or received file, whatever it turns out to be.
 *
 * One entry point above the domain layer, because the formats are not measured in the same
 * place. Three of them are plain data and are read by pure parsers in `domain/`, which
 * knows nothing of three.js. Three are not:
 *
 * - **A toolpath** is a program, not a description of a shape, and the only reader of one
 *   here is three's `GCodeLoader`, which produces geometry rather than measurements.
 * - **A 3MF** is a zip of XML, so reading one means a decompressor and an XML parser. That
 *   is the cost this project declined to put in the domain layer, and `3MFLoader` already
 *   pays it.
 * - **A glTF** holds a scene graph whose nodes carry transforms, so where its surfaces
 *   actually are is not a question its buffers answer on their own.
 *
 * Measuring any of the three means building it, and that cannot live in a layer defined by
 * having no renderer in it. So it lives here, one storey up, and callers do not care: they
 * hand over bytes and get back either the statistics to store or something to tell the
 * user.
 *
 * This is asynchronous for one reason: `GLTFLoader` is. Every other loader here parses and
 * returns, but glTF resolves its buffers and materials through promises, so there is no
 * synchronous route to a measurement of one.
 */

import type { BufferGeometry } from 'three';

import { explainGltfRefusal } from './domain/gltf.js';
import {
  detectModelKind,
  validateModel,
  type ModelValidation,
} from './domain/modelFile.js';
import type { Bounds, FileColourFormat, ModelKind, ModelStats } from './domain/types.js';
import { logger } from './log.js';
import { buildScene } from './viewer/sceneFile.js';
import { hasOwnColours, mergeParts } from './viewer/meshParts.js';
import { readToolpath } from './viewer/toolpath.js';

const log = logger('models');

export async function readModelFile(buffer: ArrayBuffer): Promise<ModelValidation> {
  const kind = detectModelKind(buffer);

  if (kind === 'gcode') return readGcode(buffer);
  if (kind === '3mf' || kind === 'gltf') return readScene(buffer, kind);

  return validateModel(buffer);
}

function readGcode(buffer: ArrayBuffer): ModelValidation {
  const text = new TextDecoder('utf-8', { fatal: false }).decode(buffer);

  // Guarded, unlike the pure parsers: rebuilding an object runs a good deal of geometry
  // over whatever a slicer happened to emit, and a file that defeats it should be refused
  // with something to read rather than take the upload down with it.
  let reading;
  try {
    reading = readToolpath(text);
  } catch (error) {
    log.error('A toolpath could not be rebuilt.', error);
    return { ok: false, message: 'This G-code could not be read.' };
  }

  // The geometry is dropped here. The renderer builds its own when the model is displayed,
  // which may be days later or never, and holding a print's worth of line segments against
  // that possibility is not worth it.
  return reading.ok
    ? { ok: true, stats: reading.stats }
    : { ok: false, message: reading.message };
}

/**
 * Measure a container by building what is inside it and then letting go of it.
 *
 * The same bargain the toolpath makes, for the same reason: the model may never be looked
 * at, and an assembly's worth of triangles is a great deal to hold against the possibility.
 * What is kept is the handful of numbers describing it — including whether it carries
 * colours of its own, which the interface needs long before the model is next built.
 */
async function readScene(
  buffer: ArrayBuffer,
  kind: Extract<ModelKind, '3mf' | 'gltf'>,
): Promise<ModelValidation> {
  // Asked before the loader is, because the loader's failure on one of these is a rejected
  // promise from somewhere deep inside it, and what comes back says nothing a person could
  // act on.
  if (kind === 'gltf') {
    const refusal = explainGltfRefusal(buffer);
    if (refusal !== undefined) return { ok: false, message: refusal };
  }

  let scene;
  try {
    scene = await buildScene(buffer, kind);
  } catch (error) {
    log.error(`A ${kind} file could not be read.`, error);
    return { ok: false, message: `This ${describe(kind)} file could not be read.` };
  }

  const fileColours: FileColourFormat = hasOwnColours(scene) ? 'material' : 'none';
  const geometry = mergeParts(scene);
  const positions = geometry.getAttribute('position').count;

  if (positions === 0) {
    geometry.dispose();
    return {
      ok: false,
      message: `This ${describe(kind)} file holds no shapes that could be drawn.`,
    };
  }

  const stats: ModelStats = {
    kind,
    // Both are binary containers. A `.gltf` is a JSON document, but its geometry is not in
    // the document — it is in the buffers the document points at, which are bytes.
    encoding: 'binary',
    triangleCount: Math.floor(positions / 3),
    bounds: boundsOf(geometry),
    fileColours,
  };

  geometry.dispose();
  return { ok: true, stats };
}

/**
 * The extent of a built geometry, in the shape the rest of the application stores.
 *
 * An empty box cannot occur here — the caller has already refused a scene with no
 * positions in it — but three types the bounding box as nullable, so the absence is
 * answered rather than asserted away.
 */
function boundsOf(geometry: BufferGeometry): Bounds {
  geometry.computeBoundingBox();
  const box = geometry.boundingBox;
  if (!box) return { min: [0, 0, 0], max: [0, 0, 0] };

  return {
    min: [box.min.x, box.min.y, box.min.z],
    max: [box.max.x, box.max.y, box.max.z],
  };
}

function describe(kind: '3mf' | 'gltf'): string {
  return kind === '3mf' ? '3MF' : 'glTF';
}
